import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Phone, Search } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbScriptTag } from "@/lib/breadcrumbSchema";
import { SCHEMA_ORG_ID, SITE_URL } from "@/lib/entitySchema";

const CANONICAL = `${SITE_URL}/faq`;

type Category =
  | "SEO"
  | "GEO AI"
  | "Web Design"
  | "Google Maps"
  | "PPC"
  | "Working With Us";

type FAQ = { q: string; a: string; category: Category };

const FAQS: FAQ[] = [
  // SEO
  {
    category: "SEO",
    q: "How long does SEO take to work for a Colorado business?",
    a: "Most Colorado businesses start seeing measurable ranking movement within 60–90 days, with significant results by months 4–6. Local SEO for Google Maps often moves faster than organic rankings. The timeline depends on your competition level, your domain age, and how aggressively we build content. We set realistic expectations on day one — not vanity promises that get you locked in and then disappointed.",
  },
  {
    category: "SEO",
    q: "How much does SEO cost in Colorado Springs or Denver?",
    a: "Our SEO is included in our Growth Marketing package at $1,650/month, which also covers Google Maps, GEO AI optimization, and social media. We don't offer cheap $300/month SEO because it simply doesn't produce results — we've watched businesses waste thousands over years with zero movement. We charge a fair rate and actually move the needle.",
  },
  {
    category: "SEO",
    q: "Do you guarantee first page Google rankings?",
    a: "No ethical SEO agency can guarantee specific rankings — Google doesn't allow it, and anyone promising it is misleading you. What we do guarantee is a transparent process, honest monthly reporting showing real movement, and the ability to leave at any time if we're not delivering. Our 97% annual client retention rate says more than any ranking guarantee would.",
  },
  {
    category: "SEO",
    q: "What's the difference between local SEO and national SEO?",
    a: "Local SEO targets customers in a specific geographic area — \"plumber Colorado Springs\" instead of just \"plumber.\" It includes Google Maps optimization, Google Business Profile management, and local citation building. National SEO targets broader, non-location-specific keywords. For most Colorado home service and contractor businesses, local SEO is where you should start.",
  },
  {
    category: "SEO",
    q: "Do you do SEO for businesses outside Colorado Springs?",
    a: "Yes — we serve all of Colorado from our office at 3210 E Woodmen Rd STE 110 in Colorado Springs. Denver, Fort Collins, Boulder, Pueblo, Grand Junction, Aurora, Greeley, and everywhere in between. We're Colorado-only by choice, which means we understand every Colorado market far better than a national agency ever could.",
  },

  // GEO AI
  {
    category: "GEO AI",
    q: "What is GEO AI optimization?",
    a: "GEO AI (Generative Engine Optimization) is the process of optimizing your website and online presence so that AI platforms like ChatGPT, Google Gemini, Perplexity, and Google AI Overviews cite your business in their answers. When someone asks an AI \"who's the best HVAC company in Colorado Springs?\" — GEO AI is what gets your business recommended in that answer instead of a competitor's.",
  },
  {
    category: "GEO AI",
    q: "Is GEO AI really necessary in 2026?",
    a: "Yes — and urgently. A growing percentage of purchase decisions now start with an AI query rather than a traditional Google search. Businesses that get cited in AI answers now will have a compounding head start over competitors who wait another year or two to figure this out. We've been optimizing for AI search since 2023.",
  },
  {
    category: "GEO AI",
    q: "How do you get my business cited in ChatGPT?",
    a: "Through structured content (FAQ formatting, schema markup), entity building (consistent business information across the web), authoritative local citations, and Google Business Profile optimization. We format your entire digital presence in the specific way that LLMs trust and reference when generating answers.",
  },
  {
    category: "GEO AI",
    q: "Can you track whether my business shows up in AI search?",
    a: "Yes — we monitor AI visibility as part of our monthly reporting. We track whether your business is being cited across the major AI platforms and show you exactly what's improving each month. This is part of every Growth Marketing plan.",
  },
  {
    category: "GEO AI",
    q: "Which AI platforms does GEO AI optimization cover?",
    a: "ChatGPT, Google AI Overviews, Google Gemini, Perplexity, Microsoft Copilot, and Apple Intelligence — the platforms your Colorado customers are actively using to find local businesses right now.",
  },

  // Web Design
  {
    category: "Web Design",
    q: "How much does a business website cost in Colorado?",
    a: "Our Starter Website is $1,850 one-time for 4 pages and our Pro Website is $2,750 for up to 10 pages — you own it outright, forever. No monthly platform fees, no ransom if you decide to leave. More complex builds are custom quoted. We'll always tell you what you actually need, not what generates the most revenue for us.",
  },
  {
    category: "Web Design",
    q: "How long does it take to build a website?",
    a: "Typically 3–5 weeks from kickoff to launch for a standard 5–10 page site. Most delays happen waiting on client-provided content like photos and copy. If you need us to write the content, we can do that — ask about our content add-on.",
  },
  {
    category: "Web Design",
    q: "Do I own my website after it's built?",
    a: "Yes — always and completely. You own the domain, the code, and every file from day one. We never hold websites hostage. If you leave us tomorrow, you walk away with everything intact.",
  },
  {
    category: "Web Design",
    q: "What platform do you build on?",
    a: "We build on modern, fast platforms — primarily custom-coded or framework-based builds, not bloated page builders. Page speed is an SEO ranking factor, so every site we deliver loads fast on both mobile and desktop.",
  },

  // Google Maps
  {
    category: "Google Maps",
    q: "How do I rank in the Google Map Pack in Colorado Springs?",
    a: "The three main factors Google uses are proximity (how close you are to the searcher), relevance (how well your Google Business Profile matches the search), and prominence (reviews, citations, activity frequency). We optimize all three — GBP content, photo strategy, consistent posting, review generation, and local citation building.",
  },
  {
    category: "Google Maps",
    q: "How many Google reviews do I need to rank in Colorado?",
    a: "There's no magic number, but recency matters as much as quantity. A business with 50 reviews from the last 6 months will typically outrank one with 200 reviews from 3 years ago. We build a consistent, ongoing review strategy — not just a one-time spike that fades.",
  },
  {
    category: "Google Maps",
    q: "Can you manage my Google Business Profile for me?",
    a: "Yes — full Google Business Profile management is included in our Growth Marketing package at $1,650/month. We optimize your profile, post 3x per week, respond to reviews, add geo-tagged photos, and monitor for any issues that could affect your Map Pack rankings.",
  },

  // PPC
  {
    category: "PPC",
    q: "How much should I spend on Google Ads in Colorado?",
    a: "It depends on your industry and market. HVAC in Colorado Springs typically needs $1,500–$3,000/month in ad spend to compete. Roofing in Denver can run higher. We won't recommend a budget we don't believe will generate positive ROI — and we'll tell you honestly upfront if PPC isn't the right move for your current stage of business.",
  },
  {
    category: "PPC",
    q: "Do Google Ads actually work for small Colorado businesses?",
    a: "Yes — when built correctly. Most small business Google Ads campaigns fail because of poor keyword targeting, no conversion tracking, and weak landing pages. We build campaigns end-to-end: the ad, the landing page, and full call tracking — so every dollar spent is fully accountable.",
  },

  // Working With Us
  {
    category: "Working With Us",
    q: "Can I actually talk directly to the owner?",
    a: "Yes — always. Chris Heidlebaugh is the owner and the person building your campaigns. You'll have his direct line. No account managers, no ticketing systems, no \"someone will follow up with you.\" Call us right now at (719) 419-3935.",
  },
  {
    category: "Working With Us",
    q: "Do you require long-term contracts?",
    a: "No. All of our SEO and marketing services are month-to-month. We believe you should stay because of results — not because you're legally stuck. Our 97% annual retention rate is built entirely on performance.",
  },
  {
    category: "Working With Us",
    q: "Where are you located?",
    a: "We're headquartered at 3210 E Woodmen Rd STE 110, Colorado Springs, CO 80920. We serve clients across all of Colorado — Denver, Fort Collins, Boulder, Pueblo, Grand Junction, Aurora, Greeley, Durango, and beyond.",
  },
  {
    category: "Working With Us",
    q: "How fast do you respond to clients?",
    a: "Our average response time is under 1 hour during business hours. You reach a real person — usually Chris directly. No offshore support, no chatbot, no queue.",
  },
  {
    category: "Working With Us",
    q: "What makes you different from other Colorado Springs marketing agencies?",
    a: "Three things: you work directly with the owner (not a junior account manager), we're the only Colorado Springs agency offering dedicated GEO AI optimization, and we publish all our prices publicly instead of running you through a sales pitch. We're not the biggest agency in the Springs — but for local businesses serious about growth, we're the most effective.",
  },
  {
    category: "Working With Us",
    q: "What's your address and phone number?",
    a: "Colorado Web Impressions | 3210 E Woodmen Rd STE 110, Colorado Springs, CO 80920 | (719) 419-3935 | coloradowebimpressions.com",
  },
];

const CATEGORIES: Category[] = [
  "SEO",
  "GEO AI",
  "Web Design",
  "Google Maps",
  "PPC",
  "Working With Us",
];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL}#faq`,
  url: CANONICAL,
  inLanguage: "en-US",
  publisher: { "@id": SCHEMA_ORG_ID },
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a,
    },
  })),
};

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      {
        title:
          "Digital Marketing FAQ | Colorado Web Impressions",
      },
      {
        name: "description",
        content:
          "Honest answers on SEO, GEO AI, web design, Google Maps & PPC — from Chris Heidlebaugh, owner of Colorado Web Impressions.",
      },
      {
        property: "og:title",
        content: "Digital Marketing FAQ — Real Answers, No Runaround",
      },
      {
        property: "og:description",
        content:
          "Honest answers from the owner of Colorado Web Impressions on SEO, GEO AI, web design, Google Maps, and PPC.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Digital Marketing FAQ — Real Answers, No Runaround" },
      { name: "twitter:description", content: "Honest answers on SEO, GEO AI, web design, Google Maps & PPC — from Chris Heidlebaugh, owner of Colorado Web Impressions." },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(FAQ_LD),
      },
      breadcrumbScriptTag([
        { name: "Home", path: "/" },
        { name: "FAQ" },
      ]),
    ],
  }),
  component: FAQPage,
});

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function FAQPage() {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<"All" | Category>("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => {
      if (activeCategory !== "All" && f.category !== activeCategory)
        return false;
      if (!q) return true;
      return (
        f.q.toLowerCase().includes(q) ||
        f.a.toLowerCase().includes(q) ||
        f.category.toLowerCase().includes(q)
      );
    });
  }, [query, activeCategory]);

  // Group by category for display
  const grouped = useMemo(() => {
    const map = new Map<Category, FAQ[]>();
    for (const f of filtered) {
      const arr = map.get(f.category) ?? [];
      arr.push(f);
      map.set(f.category, arr);
    }
    return map;
  }, [filtered]);

  const allPills: Array<"All" | Category> = ["All", ...CATEGORIES];

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Frequently Asked Questions"
        title={
          <>
            Digital Marketing FAQ —{" "}
            <span className="text-brand-gold">
              Real Answers, No Runaround
            </span>
          </>
        }
        description="Everything Colorado business owners ask us before working together — answered honestly by Chris Heidlebaugh, owner of Colorado Web Impressions, Colorado Springs."
      >
        <div className="flex justify-center">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "FAQ" }]} />
        </div>
      </PageHero>

      {/* Search + filter */}
      <section className="border-b border-border bg-section-alt">
        <div className="mx-auto max-w-5xl px-5 py-8 lg:px-8">
          <label htmlFor="faq-search" className="sr-only">
            Search questions
          </label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
            />
            <input
              id="faq-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions — e.g. 'how long does SEO take'…"
              className="w-full rounded-xl border border-border bg-card py-3.5 pl-12 pr-4 text-base text-foreground shadow-card outline-none transition-smooth placeholder:text-muted-foreground focus:border-brand-gold/60 focus:ring-2 focus:ring-brand-gold/30"
            />
          </div>

          <ul
            className="mt-5 flex flex-wrap gap-2"
            aria-label="Filter by category"
          >
            {allPills.map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <li key={cat}>
                  <button
                    type="button"
                    onClick={() => setActiveCategory(cat)}
                    aria-pressed={isActive}
                    className={
                      "inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-bold transition-smooth " +
                      (isActive
                        ? "border-brand-gold bg-gradient-gold text-brand-black shadow-gold"
                        : "border-border bg-card text-foreground hover:border-brand-gold/60 hover:text-brand-gold-deep")
                    }
                  >
                    {cat}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* FAQ accordion */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-card">
              <p className="text-base font-bold text-foreground">
                No questions match your search.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Try a different keyword — or call Chris directly at{" "}
                <a
                  href="tel:+17194193935"
                  className="font-bold text-brand-gold-deep hover:underline"
                 aria-label="Call (719) 419-3935">
                  (719) 419-3935
                </a>
                .
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {CATEGORIES.filter((c) => grouped.has(c)).map((cat) => {
                const items = grouped.get(cat)!;
                return (
                  <div key={cat}>
                    <h2
                      id={`cat-${slugify(cat)}`}
                      className="text-xs font-black uppercase tracking-[0.2em] text-brand-gold-deep"
                    >
                      {cat}
                    </h2>
                    <div className="mt-4 divide-y divide-border rounded-2xl border border-border bg-card shadow-card">
                      {items.map((f) => (
                        <details key={f.q} className="group px-5 sm:px-6">
                          <summary className="flex cursor-pointer list-none items-start justify-between gap-4 py-5 text-left text-base font-bold text-foreground [&::-webkit-details-marker]:hidden">
                            <span>{f.q}</span>
                            <span className="mt-1 shrink-0 text-brand-gold-deep transition-transform group-open:rotate-45">
                              +
                            </span>
                          </summary>
                          <p className="faq-answer pb-5 pt-0 text-base leading-relaxed text-foreground/85">
                            {f.a}
                          </p>
                        </details>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-section-alt py-20">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Still have questions?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Talk directly with Chris — owner of Colorado Web Impressions. No
            account managers, no sales pitch.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a Free Proposal
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
